export default async function (str: string) {
  const darkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h: number, s: number, l: number = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        default: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    return [h, s, l];
  };

  const hslToRgb = (h: number, s: number, l: number) => {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  };

  const intToARGB = (i: number) => {
    let r = ((i >> 24) & 0xFF);
    let g = ((i >> 16) & 0xFF);
    let b = ((i >> 8) & 0xFF);

    const [h, s, l] = rgbToHsl(r, g, b);
    const saturation = Math.min(s * 7, 1);
    [r, g, b] = hslToRgb(h, saturation, l);

    if (darkMode) {
      r = Math.floor(r / 2) + 128;
      g = Math.floor(g / 2) + 128;
      b = Math.floor(b / 2) + 128;
    } else {
      r = Math.floor(r / 2);
      g = Math.floor(g / 2);
      b = Math.floor(b / 2);
    }

    return `rgb(${r},${g},${b})`;
  };

  return intToARGB(await hash(str));
}

async function hash(input: string): Promise<number> {
  const msgUint8 = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // We're losing precision here which is fine
  const number = hashArray.slice(0, 8).reduce((num, byte) => (num * 256) + byte, 0);
  return number;
}